import { ArrowRight } from 'lucide-react'

import { Icon } from '@/common/components/Icon'
import { Card } from '@/components/ui'
import { cn } from '@/lib/utils'

interface TicketTypeItem {
  name: string
  description: string
  count: number
}

interface TicketTypesWidgetProps {
  className?: string
  tickets?: TicketTypeItem[]
  subtitle?: string
  onArrowClick?: () => void
}

export const TicketTypesWidget: React.FC<TicketTypesWidgetProps> = ({
  className,
  tickets,
  subtitle = 'Last 6 Months',
  onArrowClick,
}) => {
  return (
    <Card
      className={cn(
        'flex overflow-hidden border-2 border-white/10 bg-black/60 py-[30px] pl-[30px] pr-2 backdrop-blur-sm',
        className,
      )}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Header */}
        <div className="flex items-center gap-2.5 pr-[22px]">
          <div className="flex size-11 relative shrink-0 items-center justify-center rounded-3xl border border-white/20">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/2 h-1/2 bg-accent-1/40 rounded-3xl blur-sm" />
            <Icon
              icon="ticket-3"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              className="text-accent-1"
            />
          </div>
          <div className="flex flex-1 flex-col">
            <span className="text-title-xxs text-monochrome-1">
              Ticket Types Sales
            </span>
            <span className="text-body-medium text-monochrome-2">
              {subtitle}
            </span>
          </div>
          <button
            onClick={onArrowClick}
            className="flex size-10 shrink-0 items-center justify-center rounded-3xl border border-white/20 bg-monochrome-5-50 backdrop-blur-[5px] transition-colors hover:border-white/30"
          >
            <ArrowRight className="size-[18px] text-monochrome-1" />
          </button>
        </div>

        {/* Ticket List */}
        <div className="mt-[30px] flex flex-1 flex-col gap-[15px] overflow-y-auto pr-[22px] max-h-[325px]">
          {tickets?.map((ticket, index) => (
            <TicketRow
              key={`${ticket.name}-${index}`}
              ticket={ticket}
              isLast={index === tickets.length - 1}
            />
          ))}
        </div>
      </div>
    </Card>
  )
}

const TicketRow: React.FC<{
  ticket: TicketTypeItem
  isLast: boolean
}> = ({ ticket, isLast }) => (
  <div className="flex flex-col gap-[15px]">
    <div className="flex flex-col gap-[5px]">
      <div className="flex items-center justify-between">
        <span className="text-button-m text-monochrome-1">{ticket.name}</span>
        <span className="text-button-m text-monochrome-1">
          {ticket.count.toLocaleString()}
        </span>
      </div>
      <span className="text-body-medium text-monochrome-2">
        {ticket.description}
      </span>
    </div>
    {!isLast && <div className="h-px w-full bg-white/10" />}
  </div>
)
